import React, { useCallback, useMemo, useState } from 'react';
import { ChartLineUp, Plus } from '@phosphor-icons/react';
import { AddInsightChoiceModal } from 'src/components/insights/AddInsightChoiceModal';
import { Button } from 'src/components/Button';
import {
  PostCallDataExtractionCard,
  PostCallDataExtractionCardSkeleton,
} from 'src/components/insights/PostCallDataExtractionCard';
import { PostCallDataExtractionModal } from 'src/components/insights/PostCallDataExtractionModal';
import { SelectExistingInsightsModal } from 'src/components/insights/SelectExistingInsightsModal';
import { Agent, Insight } from 'src/interfaces/agent.interface';
import { useInsights } from 'src/pages/Agents/contexts/InsightsContext';

type PostCallDataExtractionSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

enum AddMode {
  CHOICE = 'choice',
  CREATE = 'create',
  SELECT = 'select',
}

export const PostCallDataExtractionSection: React.FC<
  PostCallDataExtractionSectionProps
> = ({ agent, setAgent, loading = false }) => {
  // Add flow: choice -> create | select
  const [addMode, setAddMode] = useState<AddMode | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInsight, setEditingInsight] = useState<Insight | null>(null);

  const { getByIds, loading: insightsLoading } = useInsights();

  const isDisabled =
    loading || insightsLoading || (!agent.user_id && !!agent.id);

  const insightIds = useMemo(
    () => agent.post_call_insights || [],
    [agent.post_call_insights],
  );

  const insights = getByIds(insightIds);

  // "Add Call Insight" button opens the choice modal
  const handleAdd = useCallback(() => {
    setAddMode(AddMode.CHOICE);
  }, []);

  // Edit an existing insight (existing flow, unchanged)
  const handleEdit = useCallback((insight: Insight) => {
    setEditingInsight(insight);
    setEditModalOpen(true);
  }, []);

  // Close whichever modal is open
  const handleCloseModal = useCallback(() => {
    setAddMode(null);
    setEditModalOpen(false);
    setEditingInsight(null);
  }, []);

  // Choice modal: user picks "Create new"
  const handleCreateNew = useCallback(() => {
    setAddMode(AddMode.CREATE);
  }, []);

  // Choice modal: user picks "Add existing"
  const handleSelectExisting = useCallback(() => {
    setAddMode(AddMode.SELECT);
  }, []);

  const handleSave = useCallback(
    (savedInsight: Insight) => {
      setAgent((prev) => {
        const current = prev.post_call_insights || [];
        const existingIndex = current.findIndex(
          (insightId) => insightId === savedInsight.id,
        );

        const next =
          existingIndex >= 0
            ? current // ID already present, no change needed
            : [...current, savedInsight.id];

        return {
          ...prev,
          post_call_insights: next,
        };
      });
      handleCloseModal();
    },
    [handleCloseModal, setAgent],
  );

  // Handler for adding multiple existing insights from the selection modal
  const handleAddExisting = useCallback(
    (selectedInsights: Insight[]) => {
      setAgent((prev) => ({
        ...prev,
        post_call_insights: [
          ...(prev.post_call_insights || []),
          ...selectedInsights.map((insight) => insight.id),
        ],
      }));
      setAddMode(null);
    },
    [setAgent],
  );

  const handleReplace = useCallback(
    (oldId: string, newId: string) => {
      setAgent((prev) => ({
        ...prev,
        post_call_insights: (prev.post_call_insights || []).map((insightId) =>
          insightId === oldId ? newId : insightId,
        ),
      }));
      handleCloseModal();
    },
    [handleCloseModal, setAgent],
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAgent((prev) => ({
        ...prev,
        post_call_insights: (prev.post_call_insights || []).filter(
          (insightId) => insightId !== id,
        ),
      }));
      handleCloseModal();
    },
    [handleCloseModal, setAgent],
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h4>Call Insights</h4>
          <p className="text-neutral-400 text-sm">
            Configure which insights to pull from each agent call.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={isDisabled} size="small">
          <Plus className="w-4 h-4 mr-2" />
          Add Call Insight
        </Button>
      </div>

      {insights.length === 0 && insightIds.length === 0 ? (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 rounded-full bg-neutral-100">
              <ChartLineUp className="w-5 h-5 text-neutral-400" />
            </div>
          </div>
          <div>
            <h3 className="text-body font-medium text-primary-body mb-1">
              No call insights configured
            </h3>
            <p className="text-description text-neutral-400">
              Automatically capture key insights from every call.
            </p>
          </div>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAdd}
            disabled={isDisabled}
            size="small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Call Insight
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {insights.map((insight) => (
            <PostCallDataExtractionCard
              key={insight.id}
              insight={insight}
              onClick={() => handleEdit(insight)}
              disabled={isDisabled}
            />
          ))}
          {insightsLoading &&
            Array.from(
              { length: insightIds.length - insights.length },
              (_, i) => (
                <PostCallDataExtractionCardSkeleton key={`skeleton-${i}`} />
              ),
            )}
        </div>
      )}

      {/* Choice modal: pick "Create new" or "Add existing" */}
      <AddInsightChoiceModal
        isOpen={addMode === AddMode.CHOICE}
        onClose={handleCloseModal}
        onCreateNew={handleCreateNew}
        onSelectExisting={handleSelectExisting}
      />

      {/* Create-new modal (existing form, also used for editing) */}
      <PostCallDataExtractionModal
        isOpen={addMode === AddMode.CREATE || editModalOpen}
        onClose={handleCloseModal}
        agent={agent}
        insight={editingInsight}
        existingInsights={insights}
        onSave={handleSave}
        onReplace={handleReplace}
        onDelete={handleDelete}
        disabled={isDisabled}
      />

      {/* Select existing insights modal */}
      <SelectExistingInsightsModal
        isOpen={addMode === AddMode.SELECT}
        onClose={handleCloseModal}
        onAdd={handleAddExisting}
        excludeIds={insightIds}
      />
    </div>
  );
};
